// Estrategia completa de keywords para Algorithmia

// Tipos para mejor mantenibilidad
export type KeywordIntent = 'Informacional' | 'Comercial' | 'Transaccional' | 'Navegacional';
export type KeywordVolume = 'Alto' | 'Medio' | 'Bajo';

export interface PrimaryKeyword {
  keyword: string;
  volume: KeywordVolume;
  intent: KeywordIntent;
  page: string;
  placement: string[];
  priority?: number; // 1-10 para priorización
}

// Estrategia completa de keywords
export const keywordStrategy = {
  // Keywords principales - Alto volumen de búsqueda
  primary: [
    {
      keyword: "desarrollo software Córdoba",
      volume: "Alto",
      intent: "Comercial",
      page: "Home",
      placement: ["title", "h1", "meta description", "primer párrafo"],
      priority: 10
    },
    {
      keyword: "sistemas gestión empresarial Argentina",
      volume: "Alto",
      intent: "Comercial",
      page: "Servicios",
      placement: ["h2", "servicios", "features"],
      priority: 9
    },
    {
      keyword: "app mantenimiento industrial",
      volume: "Medio",
      intent: "Transaccional",
      page: "Proyectos",
      placement: ["proyectos", "descripción", "features"],
      priority: 8
    },
    {
      keyword: "software seguridad laboral",
      volume: "Medio",
      intent: "Transaccional",
      page: "Proyectos",
      placement: ["proyectos", "descripción"],
      priority: 8
    },
    {
      keyword: "PWA Argentina",
      volume: "Medio",
      intent: "Informacional",
      page: "Tecnología",
      placement: ["tecnología", "features"],
      priority: 7
    },
    {
      keyword: "consultoría tecnológica Córdoba",
      volume: "Medio",
      intent: "Comercial",
      page: "Servicios",
      placement: ["servicios", "descripción"],
      priority: 7
    },
    {
      keyword: "desarrollo React Argentina",
      volume: "Medio",
      intent: "Informacional",
      page: "Tecnología",
      placement: ["tecnología", "stack"],
      priority: 6
    },
    {
      keyword: "sistema inspecciones digitales",
      volume: "Bajo",
      intent: "Transaccional",
      page: "Proyectos",
      placement: ["proyectos", "features"],
      priority: 6
    }
  ] as PrimaryKeyword[],
  
  // Keywords secundarias - Apoyo a las principales
  secondary: [
    "PWA Argentina",
    "software seguridad laboral",
    "desarrollo React Córdoba",
    "consultoría tecnológica empresas",
    "sistema inspecciones digital",
    "gestión riesgos trabajo",
    "ERP industrial argentina",
    "transformación digital pymes",
    "aplicaciones web progresivas",
    "desarrollo app Córdoba",
    "software gestión mantenimiento",
    "sistema firma digital",
    "control de pesaje industrial",
    "mediciones ambientales laborales",
    "estudios ergonómicos Argentina",
    "app checklist seguridad",
    "software matriz de riesgo",
    "sistema gestión capacitaciones",
    "tablero kanban mantenimiento"
  ],
  
  // Long-tail keywords - Bajo volumen, alta conversión
  longTail: [
    "sistema para control de visitas con geolocalización",
    "app para inspecciones de seguridad sin internet",
    "software investigación accidentes laborales",
    "programa gestión ordenes trabajo kanban",
    "sistema firma digital documentos Argentina",
    "protocolo ergonómico resolución 886/15 software",
    "mediciones ruido iluminación registro legal",
    "software matriz de riesgo laboral",
    "aplicación checklist mantenimiento industrial",
    "sistema gestión capacitaciones empleados",
    "tablero kanban notificaciones telegram",
    "software diagrama ishikawa accidentes",
    "app control visitas geolocalización fotos",
    "sistema ordenes trabajo seguimiento 7-3-1",
    "registro pesaje camiones descarga silos"
  ],
  
  // Keywords locales - SEO Geográfico
  localSEO: [
    "desarrollo software Córdoba capital",
    "sistemas gestión empresarial Córdoba",
    "app mantenimiento Córdoba",
    "programador React Córdoba",
    "empresa software Córdoba Argentina",
    "fábrica software Córdoba",
    "desarrollo web Córdoba",
    "sistemas a medida Córdoba",
    "soluciones tecnológicas Córdoba",
    "desarrollo app Córdoba Argentina",
    "software gestión Córdoba",
    "consultoría IT Córdoba",
    "desarrollo sistemas Córdoba"
  ],
  
  // Keywords de preguntas - Para featured snippets
  questions: [
    "qué es una aplicación PWA",
    "cómo mejorar la seguridad laboral con software",
    "cuánto cuesta desarrollar un software a medida",
    "beneficios del software de gestión empresarial",
    "cómo digitalizar inspecciones de seguridad",
    "qué tecnología usar para apps empresariales",
    "cómo implementar firma digital en empresas",
    "qué es un sistema kanban",
    "cómo funciona una matriz de riesgo",
    "por qué usar PWA en industria"
  ],
  
  // Keywords de tendencia - Estacionales o emergentes
  trending: [
    "IA en mantenimiento industrial",
    "machine learning seguridad laboral",
    "blockchain firma digital",
    "IoT inspecciones industriales",
    "gemelo digital mantenimiento",
    "IA generativa documentación técnica"
  ]
};

// Keywords específicas por servicio (enriquecidas)
export const serviceKeywords = {
  sistemaInspecciones: {
    primary: "sistema inspecciones digitales",
    keywords: [
      "app inspecciones mantenimiento industrial",
      "checklist digital seguridad",
      "pwa inspecciones offline",
      "software control mantenimiento",
      "sistema inspecciones técnicas",
      "app checklist industrial",
      "inspecciones sin internet",
      "inspecciones geolocalizadas",
      "checklist digital fotos",
      "app inspecciones industria"
    ],
    related: ["seguridad industrial", "mantenimiento preventivo", "auditorías"]
  },
  
  gestionRiesgos: {
    primary: "software gestión riesgos laborales",
    keywords: [
      "matriz de riesgo software",
      "identificación peligros laborales",
      "evaluación riesgos trabajo",
      "plan acción correctiva",
      "software gestión riesgos",
      "mapa de riesgo digital",
      "análisis de riesgo laboral",
      "app matriz riesgo",
      "software 5 porqués",
      "diagrama ishikawa digital"
    ],
    related: ["higiene seguridad", "prevención riesgos", "safety management"]
  },
  
  ordenesTrabajo: {
    primary: "gestión ordenes trabajo kanban",
    keywords: [
      "sistema ordenes trabajo",
      "tablero kanban digital",
      "gestión tareas mantenimiento",
      "notificaciones telegram trabajo",
      "app órdenes trabajo",
      "software mantenimiento industrial",
      "gestión ot empresarial",
      "sistema ot seguimiento",
      "app kanban industrial",
      "ordenes trabajo 7-3-1"
    ],
    related: ["mantenimiento industrial", "gestión tareas", "work orders"]
  },
  
  rrhh: {
    primary: "sistema gestión empleados",
    keywords: [
      "sistema gestión empleados",
      "registro capacitaciones personal",
      "búsqueda por dni empleados",
      "legajo digital empleado",
      "software rrhh argentina",
      "gestión recursos humanos digital",
      "app empleados empresas",
      "sistema rrhh pyme",
      "control ausentismo",
      "gestión vacaciones"
    ],
    related: ["recursos humanos", "gestión talento", "employee management"]
  },
  
  firmaDigital: {
    primary: "sistema firma digital argentina",
    keywords: [
      "sistema firma digital argentina",
      "firma electrónica documentos",
      "trazabilidad firmas",
      "app firma digital",
      "certificado firma digital",
      "validación documentos digitales",
      "firma digital geolocalizada",
      "app firma documentos",
      "firma digital ong",
      "contratos digitales"
    ],
    related: ["documentos legales", "contratos", "validación"]
  },
  
  estudiosErgonomicos: {
    primary: "estudios ergonómicos argentina",
    keywords: [
      "estudios ergonómicos argentina",
      "protocolo ergonómico 886/15",
      "software ergonomía laboral",
      "evaluación ergonómica puestos",
      "app ergonomía empresas",
      "protocolo ergonomía digital",
      "software ergonomía cálculo",
      "app evaluación ergonómica",
      "estudio puesto trabajo",
      "riesgos ergonómicos"
    ],
    related: ["ergonomía", "salud laboral", "prevención"]
  },
  
  medicionesAmbientales: {
    primary: "mediciones ambientales laborales",
    keywords: [
      "mediciones ruido laboral",
      "control iluminación puestos",
      "higiene seguridad argentina",
      "software mediciones ambientales",
      "registro ruido industrial",
      "app luxometro digital",
      "mediciones temperatura",
      "software luxometro",
      "sonometría digital",
      "app decibelímetro"
    ],
    related: ["higiene ambiental", "seguridad industrial", "salud ocupacional"]
  },
  
  controlPesaje: {
    primary: "control pesaje industrial",
    keywords: [
      "control pesaje industrial",
      "software balanzas",
      "registro cargas descargas",
      "sistema pesaje camiones",
      "app control peso",
      "registro kilos silos",
      "software básculas",
      "control pesaje cereal",
      "app pesaje industrial",
      "sistema gestión silos"
    ],
    related: ["agricultura", "industria", "logística"]
  }
} as const;

// Cluster de contenido por categoría
export const contentClusters = {
  desarrolloSoftware: {
    main: "desarrollo software Córdoba",
    description: "Desarrollo de software a medida en Córdoba, Argentina",
    related: [
      "desarrollo app Córdoba",
      "sistemas web Córdoba",
      "programación React Córdoba",
      "fábrica software Córdoba",
      "desarrollo sistemas gestión",
      "app empresariales Córdoba"
    ],
    internalLinks: ["/", "/servicios", "/tecnologia"]
  },
  
  gestionEmpresarial: {
    main: "sistemas gestión empresarial Argentina",
    description: "Software de gestión para empresas argentinas",
    related: [
      "erp argentina",
      "software administración empresas",
      "sistema gestión negocios",
      "plataforma gestión integral",
      "software contable argentina",
      "gestión pymes"
    ],
    internalLinks: ["/servicios", "/proyectos", "/#gestion"]
  },
  
  seguridadIndustrial: {
    main: "software seguridad laboral",
    description: "Soluciones digitales para seguridad industrial y laboral",
    related: [
      "app mantenimiento industrial",
      "sistema inspecciones seguridad",
      "gestión riesgos trabajo",
      "control visitas empresas",
      "investigación accidentes",
      "mediciones ambientales"
    ],
    internalLinks: ["/proyectos", "/servicios", "/#seguridad"]
  },
  
  tecnologiaPWA: {
    main: "PWA Argentina",
    description: "Progressive Web Apps para empresas argentinas",
    related: [
      "aplicaciones progresivas",
      "desarrollo pwa",
      "app offline",
      "web app Argentina",
      "react pwa",
      "tecnología web moderna"
    ],
    internalLinks: ["/tecnologia", "/proyectos", "/#pwa"]
  }
};

// URLs recomendadas para cada keyword principal
export const urlMapping = {
  "desarrollo software Córdoba": "/",
  "sistemas gestión empresarial Argentina": "/#servicios",
  "app mantenimiento industrial": "/#proyectos",
  "software seguridad laboral": "/#proyectos",
  "PWA Argentina": "/#tecnologia",
  "consultoría tecnológica Córdoba": "/#servicios",
  "desarrollo React Argentina": "/#tecnologia",
  "sistema inspecciones digitales": "/#proyectos"
} as const;

// Helper functions útiles
export const getAllKeywords = (): string[] => {
  const all = [
    ...keywordStrategy.primary.map(k => k.keyword),
    ...keywordStrategy.secondary,
    ...keywordStrategy.longTail,
    ...keywordStrategy.localSEO,
    ...keywordStrategy.questions,
    ...keywordStrategy.trending
  ];
  const uniqueSet = new Set<string>(all);
  return Array.from(uniqueSet);
};

export const getKeywordsByIntent = (intent: KeywordIntent): string[] => {
  return keywordStrategy.primary
    .filter(k => k.intent === intent)
    .map(k => k.keyword);
};

export const getKeywordsByPage = (page: string): string[] => {
  return keywordStrategy.primary
    .filter(k => k.page === page)
    .map(k => k.keyword);
};

export const getTopPriorityKeywords = (limit: number = 10): string[] => {
  return keywordStrategy.primary
    .sort((a, b) => (b.priority || 0) - (a.priority || 0))
    .slice(0, limit)
    .map(k => k.keyword);
};

// Versión plana para meta tags (limitada)
export const getMetaKeywords = (limit: number = 10): string => {
  const topKeywords = getTopPriorityKeywords(limit);
  return topKeywords.join(', ');
};

export default keywordStrategy;